/**
 * Created by Ning Jiang on 10/31/2019.
 */
import {MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Elastic, Power3, TimelineLite, TweenLite} from "gsap";
import {DeadwoodSoundConfig} from "../DeadwoodSoundConfig";
import {DeadwoodReelSymbol} from "./DeadwoodReelSymbol";
import {DeadwoodGameConfig} from "../DeadwoodGameConfig";

export class DeadwoodStackedWild extends DeadwoodReelSymbol {

    private static _multiplierTopOffset:number = -30;
    private static _multiplierBottomOffset:number = 148;

    private _multiplierTop:MultiSprites;
    private _multiplierBottom:MultiSprites;
    private _multiplierGlows:TimelineSprite[];
    private _multiplierPtSprite:PIXI.Sprite;
    private _multiplierPtSpine:PIXI.spine.Spine;

    private _currentMultiplier:number = 1;

    private _holesContainer:PIXI.Container;
    private _hasGunShot:boolean = false;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    protected initAnimations():void {
        if(!this._multiplierTop) {
            this._multiplierTop = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("wildMulti"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("wildMulti_spin"),
                    anchor : [0.5, 0.5]
                }
            });
            this._multiplierTop.position.set(0, -this.stackedSymName!.index * GameConfig.instance.SYMBOL_HEIGHT + DeadwoodStackedWild._multiplierTopOffset);
        }

        if(!this._multiplierBottom) {
            this._multiplierBottom = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("wildMulti"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("wildMulti_spin"),
                    anchor : [0.5, 0.5]
                }
            });
            this._multiplierBottom.position.set(0, (2 - this.stackedSymName!.index) * GameConfig.instance.SYMBOL_HEIGHT + DeadwoodStackedWild._multiplierBottomOffset);
        }

        this._multiplierGlows = [];
        this._holesContainer = new PIXI.Container();
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        const result:boolean = super.initState(stateConfig);

        if(this._multiplierTop) {
            this.addChild(this._multiplierTop);
        }
        if(this._multiplierBottom) {
            this.addChild(this._multiplierBottom);
        }

        if(this._multiplierGlows.length > 0) {
            this._multiplierGlows.forEach((glow:TimelineSprite) => {
                this.addChild(glow);
            })
        }

        if(this._multiplierPtSprite) {
            this.addChild(this._multiplierPtSprite);
        }

        if(this._holesContainer) {
            this.addChild(this._holesContainer);
        }

        return result;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
            timeline.add(() => {this.updateMultiplier(this._currentMultiplier, false, "spin", newState.fadeInDuration, newState.fadeOutDuration);}, delay);
        } else {
            timeline.add(() => {this.updateMultiplier(this._currentMultiplier, false, "normal", newState.fadeInDuration, newState.fadeOutDuration);}, delay);
        }

        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.noWin!.keyword) {
            for(let spriteName in this._multiplierTop.sprites) {
                const toSprite:TimelineSprite = this._multiplierTop.sprites[spriteName];
                timeline.add(TweenLite.to(toSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                const bottomSprite:TimelineSprite = this._multiplierBottom.sprites[spriteName];
                timeline.add(TweenLite.to(bottomSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
            }
        } else {
            for(let spriteName in this._multiplierTop.sprites) {
                const toSprite:TimelineSprite = this._multiplierTop.sprites[spriteName];
                timeline.add(TweenLite.to(toSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                const bottomSprite:TimelineSprite = this._multiplierBottom.sprites[spriteName];
                timeline.add(TweenLite.to(bottomSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
            }
        }

        // no need to do more state, because there will be no other state when there are holes.
        //TODO: if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
        //     this._holesContainer.children.forEach((hole:PIXI.Sprite) => {
        //         hole.texture = GameResources.getTextures("bulletHole_spin")[0];
        //         hole.rotation = 0;
        //     })
        // }
    }

    public updateMultiplier(multiplier:number, isAnimated:boolean, spriteName:string, fadeInDuration:number = 0.1, fadeOutDuration:number = 0.1):TimelineLite {
        isAnimated = this.visible ? isAnimated : false;
        if(isAnimated) {
            this.createMultiplierEffects();
        }
        this._currentMultiplier = multiplier;
        const index:number = this._currentMultiplier;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            this._multiplierTop.playSpriteFrames(spriteName, {
                    duration : fadeInDuration,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : fadeOutDuration,
                    vars : [{toVars : {alpha : 0}}]
                },
                [index, index]),
            this._multiplierBottom.playSpriteFrames(spriteName, {
                    duration : fadeInDuration,
                    vars : [{toVars : {alpha : 1}}]
                },
                {
                    duration : fadeOutDuration,
                    vars : [{toVars : {alpha : 0}}]
                },
                [index, index])
        ], 0);
        if(isAnimated) {
            timeline.add(() => {
                TweenLite.fromTo(this._multiplierTop.scale, 0.3,
                    {x : 1.5, y : 1.5, ease : Elastic.easeIn.config(1, 1)},
                    {x : 1, y : 1});
                TweenLite.fromTo(this._multiplierBottom.scale, 0.3,
                    {x : 1.5, y : 1.5, ease : Elastic.easeIn.config(1, 1)},
                    {x : 1, y : 1});
                this._multiplierGlows.forEach((glow:TimelineSprite) => {
                    glow.getAnimation([index, index]);
                    TweenLite.fromTo(glow.scale, 0.4,
                        {x : 1.4, y : 1.4, ease : Elastic.easeIn.config(1, 1)},
                        {x : 1, y : 1});
                    TweenLite.fromTo(glow, 0.6, {alpha : 1}, {alpha : 0, ease : Power3.easeIn});
                });
                this._multiplierPtSpine.state.clearTrack(0);
                this._multiplierPtSpine.skeleton.setToSetupPose();
                this._multiplierPtSpine.state.setAnimation(0, "animation", false);
            }, 0);
        }

        return timeline;
    }

    private createMultiplierEffects():void {
        if(this._multiplierGlows.length > 0) {
            return;
        }

        this._multiplierGlows.push(this.createMultiplierGlow(this._multiplierTop));
        this._multiplierGlows.push(this.createMultiplierGlow(this._multiplierBottom));

        this._multiplierPtSprite = new PIXI.Sprite();
        this._multiplierPtSprite.anchor.set(0.5);
        this._multiplierPtSprite.y = -((this.stackedSymName!.index + 0.5) * this.symHeight
            + this.stackedSymName!.index * this.bottomPadding
            - this.stackedSymName!.totalNum * this.symHeight * 0.5
            - (this.stackedSymName!.totalNum - 1) * this.bottomPadding * 0.5);
        this.addChild(this._multiplierPtSprite);
        this._multiplierPtSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("nudgePt"));
        this._multiplierPtSpine.autoUpdate = true;
        this._multiplierPtSprite.addChild(this._multiplierPtSpine);

        if(this._holesContainer) {
            this.addChild(this._holesContainer);
        }
    }

    private createMultiplierGlow(multiplier:MultiSprites):TimelineSprite {
        const glow:TimelineSprite = new TimelineSprite(GameResources.getTextures("W_glow"));
        glow.blendMode = PIXI.BLEND_MODES.ADD;
        glow.anchor.set(0.5);
        glow.alpha = 0;
        glow.position.set(multiplier.x, multiplier.y);
        this.addChild(glow);

        return glow;
    }

    public playGunShot(animate:boolean, duration:number):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();

        // To make sure it doesn't play twice for abort wp.
        if(!this._hasGunShot) {
            timeline.add(() => this.addHole(true));
            timeline.add(() => this.addHole(animate), animate ? (duration - 0.2) : 0);
            timeline.add(() => {}, animate ? 0.2 : 0);

            this._hasGunShot = true;
        }

        return timeline;
    }

    private addHole(playSound:boolean):void {
        //this will always be the bottom symbol, so we can use the offset without checking stacked positions.
        const hole:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(Math.random() >= 0.5 ? "gunHoles1" : "gunHoles2")[0]);
        hole.anchor.set(0.5, 0.5);
        hole.rotation = Math.random() * Math.PI * 2;
        const offsetX:number = Math.round(Math.random() * (GameConfig.instance.SYMBOL_WIDTH - 60)) - GameConfig.instance.SYMBOL_WIDTH * 0.5 + 30;
        const offsetY:number = Math.round(Math.random() * (GameConfig.instance.SYMBOL_HEIGHT * 4 - 60)) - GameConfig.instance.SYMBOL_HEIGHT * 3.5 + 30;
        hole.position.set(offsetX, offsetY);
        this._holesContainer.addChild(hole);
        DeadwoodSoundConfig.playGunSound();
    }
}